import Papa from 'papaparse';
import {type ScrapyCollection} from '@/api/scrapyCollections';
import {DEFAULT_SPIDER_NAME} from '@/lib/platform';

export type CsvImportRow = {
  rowNumber: number;
  name: string;
  description: string | null;
  spiderName: string;
  spiderArgs: Record<string, string>;
  cronExpression: string | null;
  affiliate: string | null;
  policyViolation: string | null;
  language: string | null;
};

export type CsvParseResult = {
  rows: CsvImportRow[];
  warnings: string[];
};

// Parses a bulk-import CSV of Scrapy collection configs, mirroring nile-ssh's
// CollectionCsvParser. Column layout (by position):
//   1. Name            → collection name (required — rows without one are skipped)
//   2. Description     → optional description
//   3. Query           → spider arg `query`
//   4. SpiderName      → defaults to `youtube_search` when blank
//   5. Schedule        → cron expression (optional)
//   6. DaysLimit       → spider arg `days_limit`
//   7. Affiliate       → customer (optional)
//   8. PolicyViolation → moderation policy violation (optional)
//   9. Language        → reference-data language (optional)
//   10+ SpiderArgs and every column after → one `key=value` spider arg per cell
// Quoting (commas/line breaks inside quoted fields) is handled by PapaParse.
const COL_NAME = 0;
const COL_DESCRIPTION = 1;
const COL_QUERY = 2;
const COL_SPIDER_NAME = 3;
const COL_SCHEDULE = 4;
const COL_DAYS_LIMIT = 5;
const COL_AFFILIATE = 6;
const COL_POLICY_VIOLATION = 7;
const COL_LANGUAGE = 8;
const COL_SPIDER_ARGS_START = 9;

const EXPECTED_HEADER = [
  'Name',
  'Description',
  'Query',
  'SpiderName',
  'Schedule',
  'DaysLimit',
  'Affiliate',
  'Policy Violation',
  'Language'
];

function isHeaderRow(record: string[]): boolean {
  return EXPECTED_HEADER.some(
    (expected, i) => (record[i] ?? '').trim().toLowerCase() === expected.toLowerCase()
  );
}

export function parseCollectionsCsv(content: string): CsvParseResult {
  const rows: CsvImportRow[] = [];
  const warnings: string[] = [];
  const lastRowNumberByName = new Map<string, number>();

  const {data} = Papa.parse<string[]>(content, {skipEmptyLines: true});

  for (const [index, record] of data.entries()) {
    const rowNumber = index + 1;
    if (index === 0 && isHeaderRow(record)) {
      continue;
    }

    const cell = (idx: number) => (record[idx] ?? '').trim();

    const name = cell(COL_NAME);
    if (name.length === 0) {
      warnings.push(`Row ${rowNumber}: missing name.`);
      continue;
    }

    const spiderArgs: Record<string, string> = {};
    const query = cell(COL_QUERY);
    if (query.length > 0) {
      spiderArgs.query = query;
    }

    const daysLimit = cell(COL_DAYS_LIMIT);
    if (daysLimit.length > 0) {
      spiderArgs.days_limit = daysLimit;
    }

    for (let c = COL_SPIDER_ARGS_START; c < record.length; c++) {
      const pair = (record[c] ?? '').trim();
      const eq = pair.indexOf('=');
      if (eq <= 0) {
        continue;
      }

      const key = pair.slice(0, eq).trim();
      if (key.length > 0) {
        spiderArgs[key] = pair.slice(eq + 1).trim();
      }
    }

    if (!spiderArgs.query) {
      warnings.push(`Row ${rowNumber}: missing query — row skipped.`);
      continue;
    }

    const description = cell(COL_DESCRIPTION);
    const spiderName = cell(COL_SPIDER_NAME);
    const schedule = cell(COL_SCHEDULE);
    const affiliate = cell(COL_AFFILIATE);
    const policyViolation = cell(COL_POLICY_VIOLATION);
    const language = cell(COL_LANGUAGE);

    const priorRowNumber = lastRowNumberByName.get(name);
    if (priorRowNumber !== undefined) {
      warnings.push(
        `Row ${rowNumber}: duplicate name "${name}" (also row ${priorRowNumber}) — this later row's values will be used.`
      );
    }

    lastRowNumberByName.set(name, rowNumber);

    rows.push({
      rowNumber,
      name,
      description: description.length === 0 ? null : description,
      spiderName: spiderName.length === 0 ? DEFAULT_SPIDER_NAME : spiderName,
      spiderArgs,
      cronExpression: schedule.length === 0 ? null : schedule,
      affiliate: affiliate.length === 0 ? null : affiliate,
      policyViolation: policyViolation.length === 0 ? null : policyViolation,
      language: language.length === 0 ? null : language
    });
  }

  return {rows, warnings};
}

const HEADER_ROW = [
  'Name',
  'Description',
  'Query',
  'SpiderName',
  'Schedule',
  'DaysLimit',
  'Affiliate',
  'Policy Violation',
  'Language'
];

// Neutralizes CSV/DDE formula injection: a cell beginning with a formula trigger is executed by
// Excel/Sheets when the exported file is opened, so prefix an apostrophe to force text. Mirrors the
// API-side submissions CSV export.
function neutralizeCsvFormula(cell: string): string {
  return cell.length > 0 && '=+-@\t\r'.includes(cell[0] ?? '') ? `'${cell}` : cell;
}

// Serializes collections back to the same column layout `parseCollectionsCsv` reads, so an exported
// file round-trips through import (except a leading formula-trigger char, which is apostrophe-escaped
// for spreadsheet safety). `query` and `days_limit` are pulled into their dedicated columns; every
// other spider arg becomes a trailing `key=value` column.
export function exportCollectionsCsv(collections: readonly ScrapyCollection[]): string {
  const rows = collections.map((c) => {
    const {query = '', days_limit: daysLimit = '', ...rest} = c.spider_args;
    const extraArgs = Object.entries(rest).map(([key, value]) => `${key}=${value}`);

    return [
      c.name,
      c.description ?? '',
      query,
      c.spider_name,
      c.cron_expression ?? '',
      daysLimit,
      c.affiliate ?? '',
      c.policy_violation ?? '',
      c.language ?? '',
      ...extraArgs
    ].map(neutralizeCsvFormula);
  });

  return Papa.unparse([HEADER_ROW, ...rows]);
}
